import { useCallback, useEffect, useState } from "react";

import { CONFIG_CHANGED_EVENT, fetchConfig } from "../api/config";
import { PrimaryNav, type NavView } from "./PrimaryNav";

type HealthStatus = "checking" | "ready" | "down";

type VersionInfo = {
  commit: string;
  branch: string;
  dirty: boolean;
  build_at: string | null;
  deployer: string | null;
  source: string | null;
  data_bundle: {
    source?: string | null;
    data_version?: string | null;
    data_version_short?: string | null;
    matches_disk?: boolean | null;
  } | null;
};

type KeyStatus = "checking" | "present" | "missing" | "unknown";

function shortHash(value: string, length = 7): string {
  if (!value || value === "unknown") return "unknown";
  return value.length > length ? value.slice(0, length) : value;
}

export function TitleBar({
  active,
  onSelect,
}: {
  active: NavView;
  onSelect: (view: NavView) => void;
}) {
  const [health, setHealth] = useState<HealthStatus>("checking");
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [versionUnreachable, setVersionUnreachable] = useState(false);
  const [keyStatus, setKeyStatus] = useState<KeyStatus>("checking");
  const [keyHint, setKeyHint] = useState<string>("");

  const reloadKey = useCallback(async () => {
    try {
      const cfg = await fetchConfig();
      setKeyStatus(cfg.api_key_present ? "present" : "missing");
      setKeyHint(cfg.api_key_hint ?? "");
    } catch {
      setKeyStatus("unknown");
      setKeyHint("");
    }
  }, []);

  useEffect(() => {
    void reloadKey();
    const onChange = () => {
      void reloadKey();
    };
    window.addEventListener(CONFIG_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CONFIG_CHANGED_EVENT, onChange);
  }, [reloadKey]);

  useEffect(() => {
    fetch("/api/health")
      .then((r) => setHealth(r.ok ? "ready" : "down"))
      .catch(() => setHealth("down"));
  }, []);

  useEffect(() => {
    fetch("/api/version")
      .then(async (r) => {
        if (!r.ok) {
          setVersionUnreachable(true);
          return;
        }
        const v = (await r.json()) as Partial<VersionInfo>;
        if (typeof v.commit !== "string" || typeof v.branch !== "string") {
          setVersionUnreachable(true);
          return;
        }
        setVersion({
          commit: v.commit,
          branch: v.branch,
          dirty: Boolean(v.dirty),
          build_at: v.build_at ?? null,
          deployer: v.deployer ?? null,
          source: v.source ?? null,
          data_bundle:
            v.data_bundle && typeof v.data_bundle === "object"
              ? v.data_bundle
              : null,
        });
      })
      .catch(() => setVersionUnreachable(true));
  }, []);

  const healthText =
    health === "ready" ? "backend ready"
    : health === "down" ? "backend down"
    : "backend checking…";

  const versionText = version
    ? `${version.branch} · ${shortHash(version.commit)}${version.dirty ? "*" : ""}`
    : versionUnreachable
    ? "version: unknown"
    : "…";

  const versionTitle = version
    ? [
        `branch: ${version.branch}`,
        `commit: ${version.commit}${version.dirty ? " (dirty)" : ""}`,
        version.data_bundle?.data_version_short &&
          `data: ${version.data_bundle.data_version_short}`,
        version.data_bundle?.source &&
          `data source: ${version.data_bundle.source}`,
        typeof version.data_bundle?.matches_disk === "boolean" &&
          `data matches disk: ${version.data_bundle.matches_disk ? "yes" : "no"}`,
        version.build_at && `built: ${version.build_at}`,
        version.deployer && `deployer: ${version.deployer}`,
        version.source && `code source: ${version.source}`,
      ]
        .filter(Boolean)
        .join("\n")
    : versionUnreachable
    ? "/api/version unreachable"
    : "loading…";

  const currentViewLabel =
    active === "settings" ? "Settings" : active === "help" ? "Help" : "Studies";

  return (
    <div className="titlebar">
      <div className="brand">
        <span className="logo">D</span>
        <span className="brand-text">Virtual 7-Post AutoResearch</span>
      </div>
      <PrimaryNav active={active} onSelect={onSelect} />
      <div className="crumbs">
        <span>indy-rc</span>
        <span className="sep">/</span>
        <span>damper-ops</span>
        <span className="sep">/</span>
        <span className="current">{currentViewLabel}</span>
      </div>
      <div className="right">
        <span className="chip">
          <span className="dot" />
          <span>{healthText}</span>
        </span>
        <span
          className={
            keyStatus === "present"
              ? "chip"
              : keyStatus === "missing" || keyStatus === "unknown"
              ? "chip warn"
              : "chip"
          }
          title={
            keyStatus === "present"
              ? "Anthropic API key stored — Claude features ready"
              : keyStatus === "missing"
              ? "No Anthropic API key stored — set one on the Settings page"
              : keyStatus === "unknown"
              ? "/api/config unreachable"
              : "checking…"
          }
        >
          <span className="dot" />
          <span>
            {keyStatus === "present"
              ? `anthropic: …${keyHint}`
              : keyStatus === "missing"
              ? "anthropic: no key"
              : keyStatus === "unknown"
              ? "anthropic: unknown"
              : "anthropic: …"}
          </span>
        </span>
        <span className="chip mono version-chip" title={versionTitle}>
          <span>{versionText}</span>
        </span>
      </div>
    </div>
  );
}
